import {
  CamposConfiguracaoFormSchema,
  EquipesConfiguracaoFormSchema,
  IConfiguracao,
  ISearchConfiguracao,
  InfoConfiguracaoFormSchema,
  IntegracoesConfiguracaoFormSchema,
  MensagensConfiguracaoFormSchema,
  MidiasConfiguracaoFormSchema,
} from "@/interfaces/configuracao.interface";
import type { ConfigLojinhaDto } from "@/interfaces/print-station.interface";
import { IEventoRequest } from "@/interfaces/evento.interface";
import {
  IPaginatedRequest,
  IConfiguracaoPaginatedRequestTipo,
} from "@/interfaces/paginated.interface";
import { arrayUniqueByKey, downloadFile } from "@/lib/utils";
import { prepareImageFilesForUpload } from "@/lib/prepare-image-upload";
import { postLog } from "@/lib/logs";
import { skipGlobalErrorToast } from "@/lib/rtk-query-config";
import {
  SetConfiguracoes,
  SetGrupos,
} from "@/redux/slices/configuracoes.slice";
import type { RootState } from "@/redux/store";
import { z } from "zod";
import { createBaseApi, getBaseApiEndpointObjects } from "./base.service";
import { RESOURCES } from "./resources";

export const configuracoesApi = createBaseApi<any>(RESOURCES.CONFIGURACOES);

export const configuracoesAPI = configuracoesApi.injectEndpoints({
  endpoints: (build) => ({
    getConfiguracoesPaginated: build.query<
      any,
      IConfiguracaoPaginatedRequestTipo
    >({
      query: (params) => {
        const { moduloInfantil, ...rest } = params ?? {};
        return {
          url: `/configuracoes/paginated`,
          params: {
            ...rest,
            ...(moduloInfantil === true ||
            moduloInfantil === "true"
              ? { moduloInfantil: "true" }
              : {}),
          },
          method: "GET",
        };
      },
      providesTags: () => [{ type: "Configuracoes" }],
    }),
    extractExcelConfiguracoes: build.mutation<any, IPaginatedRequest>({
      queryFn: async ({ filename, campos }, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/configuracoes/paginated`,
          params: {
            extract: "excel",
            campos,
            filename,
          },
          method: "GET",
          responseHandler: (response) => response.blob(),
        });
        return downloadFile(result, filename);
      },
    }),
    getCentroCustosConfiguracao: build.query<any, ISearchConfiguracao>({
      query: ({ configuracaoId, search }) => ({
        url: `/configuracoes/centro-custos/${configuracaoId}`,
        params: { search },
        method: "GET",
      }),
      providesTags: () => [{ type: "CentroCustosConfiguracao" }],
    }),
    getMeiosPagamentoConfiguracao: build.query<any, ISearchConfiguracao>({
      query: ({ configuracaoId, search }) => ({
        url: `/configuracoes/meios-pagamento/${configuracaoId}`,
        params: { search },
        method: "GET",
      }),
      providesTags: () => [{ type: "MeiosPagamentoConfiguracao" }],
    }),
    postInfo: build.mutation<any, z.infer<typeof InfoConfiguracaoFormSchema>>({
      query: (body) => ({
        url: `/configuracoes/info`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [
        { type: "Configuracoes" },
        { type: "IgrejasEvento" },
        { type: "TiposEvento" },
        { type: "SelectEventos" },
        { type: "Eventos" },
      ],
    }),
    getIdentificadorDisponivel: build.query<
      { disponivel: boolean },
      { identificador: string; excludeConfiguracaoId?: number }
    >({
      query: ({ identificador, excludeConfiguracaoId }) => ({
        url: `/configuracoes/info/identificador-disponivel`,
        method: "GET",
        params: {
          identificador,
          ...(excludeConfiguracaoId != null && excludeConfiguracaoId > 0
            ? { excludeConfiguracaoId }
            : {}),
        },
      }),
      keepUnusedDataFor: 0,
    }),
    postCamposConfiguracao: build.mutation<
      any,
      z.infer<typeof CamposConfiguracaoFormSchema>
    >({
      query: (body) => ({
        url: `/configuracoes/campos`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => {
        const eventoId = window.localStorage.getItem("EventoId");
        return [
          { type: "Configuracoes" },
          { type: "Campos" },
          { type: "Infantil", id: "config-org" },
          { type: "SelectEventos", id: eventoId },
          { type: "SelectEventos" },
          { type: "Eventos" },
        ];
      },
    }),
    postEquipesEvento: build.mutation<
      any,
      z.infer<typeof EquipesConfiguracaoFormSchema> & {
        skipInvalidatesTags?: boolean;
      }
    >({
      query: (body) => {
        const { skipInvalidatesTags: _skipInvalidatesTags, ...payload } = body;
        return {
          url: `/configuracoes/equipes`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: (_result, _error, arg) => {
        if (arg?.skipInvalidatesTags) return [];
        return [
          { type: "Configuracoes" },
          { type: "EquipesFormularioEvento" },
          { type: "EquipesEvento" },
          { type: "EquipesMembros" },
          { type: "SelectEventos" },
          { type: "Eventos" },
        ];
      },
    }),
    postIntegracoes: build.mutation<
      any,
      z.infer<typeof IntegracoesConfiguracaoFormSchema>
    >({
      query: (body) => ({
        url: `/configuracoes/integracoes`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [
        { type: "Configuracoes" },
        { type: "SelectEventos" },
        { type: "Eventos" },
      ],
    }),
    postMidias: build.mutation<
      any,
      z.infer<typeof MidiasConfiguracaoFormSchema> & { EventoId?: number }
    >({
      extraOptions: skipGlobalErrorToast,
      queryFn: async (body, api, extraOptions, baseQuery) => {
        const files = await prepareImageFilesForUpload({
          logo: body.logoFile,
          logoRelatorio: body.logoRelatorioFile,
          background: body.backgroundFile,
        });
        const formData = new FormData();
        formData.append("Id", body.Id.toString());
        if (body.EventoId != null && Number(body.EventoId) > 0) {
          formData.append("EventoId", String(body.EventoId));
        }
        if (files.logo) formData.append("logo", files.logo);
        if (files.logoRelatorio) formData.append("logoRelatorio", files.logoRelatorio);
        if (files.background) formData.append("background", files.background);

        const result = await baseQuery({
            url: `/configuracoes/midias`,
            method: "POST",
            body: formData,
          });
        if (result.error) {
          void postLog({
            Tipo: "Arquivos_Falhou_Upload",
            Objeto: {
              acao: "upload",
              contexto: "post_midias",
              status: result.error.status,
            },
          });
        }
        return result;
      },
      invalidatesTags: () => {
        const eventoId = window.localStorage.getItem("EventoId");
        return [
          { type: "Configuracoes" },
          { type: "SelectEventos", id: eventoId },
          { type: "SelectEventos" },
          { type: "Eventos" },
        ];
      },
    }),
    postMensagens: build.mutation<
      any,
      z.infer<typeof MensagensConfiguracaoFormSchema>
    >({
      query: (body) => ({
        url: `/configuracoes/mensagens`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => {
        const eventoId = window.localStorage.getItem("EventoId");
        return [
          { type: "Configuracoes" },
          { type: "SelectEventos", id: eventoId },
          { type: "SelectEventos" },
          { type: "Eventos" },
        ];
      },
    }),
    getGrupos: build.query<any, number>({
      query: (eventoId) => ({
        url: `/configuracoes/grupos/${eventoId}`,
        method: "GET",
      }),
      providesTags: () => [{ type: "GruposConfiguracao" }],
      async onQueryStarted(_query, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(SetGrupos(data));
      },
    }),
    /** Configuração completa (congregações, campos, equipes) para o painel de edição do evento. */
    getConfiguracaoEdicao: build.query<Record<string, any>, { configuracaoId: number }>({
      query: ({ configuracaoId }) => ({
        url: `/configuracoes/${configuracaoId}/edicao`,
        method: "GET",
      }),
      providesTags: (_result, _error, { configuracaoId }) => [
        { type: "Configuracoes", id: `edicao-${configuracaoId}` },
      ],
    }),
    getConfigLojinha: build.query<ConfigLojinhaDto, { configuracaoId: number }>({
      query: ({ configuracaoId }) => ({
        url: `/configuracoes/${configuracaoId}/lojinha`,
        method: "GET",
      }),
      providesTags: (_result, _error, { configuracaoId }) => [
        { type: "Configuracoes", id: `lojinha-${configuracaoId}` },
      ],
    }),
    updateConfigLojinha: build.mutation<void, ConfigLojinhaDto>({
      query: (body) => ({
        url: `/configuracoes/${body.Id}/lojinha`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { Id }) => [
        { type: "Configuracoes" },
        { type: "Configuracoes", id: `lojinha-${Id}` },
        { type: "SelectEventos" },
        { type: "Eventos" },
      ],
    }),
    getSugestaoMontagemEquipes: build.query<
      any,
      { configuracaoId: number; loginId: number; dataIni?: string; dataFim?: string }
    >({
      query: ({ configuracaoId, loginId, dataIni, dataFim }) => ({
        url: `/configuracoes/${configuracaoId}/sugestao-montagem-equipes`,
        method: "GET",
        params: { loginId, dataIni, dataFim },
      }),
    }),
    salvarMontagemEquipesConfig: build.mutation<
      any,
      {
        configuracaoId: number;
        LoginId: number;
        quantidades?: { EquipeId: number; QuantidadeSugerida?: number | null }[];
        mapeamentos?: {
          EquipeOrigemId: number;
          EquipeDestinoId: number;
          Tipo?: string;
        }[];
      }
    >({
      query: ({ configuracaoId, ...body }) => ({
        url: `/configuracoes/${configuracaoId}/montagem-equipes`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [{ type: "Configuracoes" }],
    }),
    getConfigs: build.query<any, IEventoRequest>({
      queryFn: async (
        { roles, page, search, configuracaoId, loginId, perPage },
        api,
        extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/configuracoes`,
          params: {
            roles,
            page,
            search,
            perPage,
            loginId,
            configuracaoId:
              configuracaoId ||
              (api.getState() as RootState).configuracoesReducer.ConfiguracaoId,
          },
          method: "GET",
          responseHandler: (response) => response.json(),
        });
        return result;
      },
      async onQueryStarted(
        { ...patch },
        { dispatch, queryFulfilled, getState }
      ) {
        const { data } = await queryFulfilled;
        if (patch.page > 1) {
          dispatch(
            SetConfiguracoes(
              arrayUniqueByKey(
                (
                  getState() as RootState
                ).configuracoesReducer.Configuracoes.concat(data),
                "Id"
              ) as IConfiguracao[]
            )
          );
        } else {
          dispatch(SetConfiguracoes(data));
        }
      },
    }),
  }),
});

const baseEndpoints = getBaseApiEndpointObjects(
  configuracoesAPI,
  RESOURCES.CONFIGURACOES
);

export const {
  useGetConfigsQuery,
  useExtractExcelConfiguracoesMutation,
  useGetConfiguracoesPaginatedQuery,
  useGetCentroCustosConfiguracaoQuery,
  useGetMeiosPagamentoConfiguracaoQuery,
  useLazyGetIdentificadorDisponivelQuery,
  usePostInfoMutation,
  usePostEquipesEventoMutation,
  usePostCamposConfiguracaoMutation,
  usePostMensagensMutation,
  usePostIntegracoesMutation,
  usePostMidiasMutation,
  useGetConfigLojinhaQuery,
  useGetConfiguracaoEdicaoQuery,
  useLazyGetConfiguracaoEdicaoQuery,
  useUpdateConfigLojinhaMutation,
  useGetSugestaoMontagemEquipesQuery,
  useLazyGetSugestaoMontagemEquipesQuery,
  useSalvarMontagemEquipesConfigMutation,
} = configuracoesAPI;

// Exportar API com endpoints base
export { configuracoesAPI as configuracoesAPIWithBase };
export const configuracoesAPIComplete = {
  ...baseEndpoints,
  ...configuracoesAPI.endpoints,
};
